import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useNavigation } from '@react-navigation/native';

import { createCtx } from '@pletly/ui';
import { useOrganisationInfo } from '@pletly/feature-organisation';
import { User } from '@pletly/feature-auth';

import { OrganisationRole, UserRole } from '%__generated__/types';
import { useUser } from '%hooks';

export enum Scope {
	MainApp,
	OrganisationPanel,
}

type ContextType = {
	activeScope: Scope;
	changeScope: (scope: Scope) => void;
};

const ScopesRootScreens = {
	[Scope.OrganisationPanel]: 'Home',
	[Scope.MainApp]: 'Home',
} as const;

const getAvailableScopes = (role: User['role'], organisationRole?: OrganisationRole): Scope[] => {
	if (role === UserRole.OrganisationAdmin) {
		return [Scope.OrganisationPanel];
	}

	if (role === UserRole.Pal && organisationRole === OrganisationRole.Manager) {
		return [Scope.MainApp, Scope.OrganisationPanel];
	}

	return [Scope.MainApp];
};

const [useNavigationScope, NavigationScopeDataProvider] = createCtx<ContextType>();

const NavigationScopeProvider = ({ children }: { children?: ReactNode }) => {
	const { role } = useUser();
	const { organisationInfo } = useOrganisationInfo();

	const availableScopes = useMemo(
		() => getAvailableScopes(role, organisationInfo?.role),
		[role, organisationInfo],
	);

	const [activeScope, setActiveScope] = useState<Scope>(availableScopes[0]);
	const { reset } = useNavigation();

	const changeScope = useCallback(
		(scope: Scope) => {
			if (!availableScopes.includes(scope)) {
				console.error('Scope is not available for this user');

				return;
			}

			setActiveScope(scope);
			reset({ index: 0, routes: [{ name: ScopesRootScreens[scope] }] });

			if (scope === Scope.MainApp) {
				window.location.replace('/');
			}
		},
		[reset, availableScopes],
	);

	const value = useMemo(
		() => ({
			activeScope,
			changeScope,
		}),
		[activeScope, changeScope],
	);

	return <NavigationScopeDataProvider value={value}>{children}</NavigationScopeDataProvider>;
};

export { useNavigationScope, NavigationScopeProvider };
